/**
 * Ad click-ID capture.
 *
 * When a user clicks a Reddit ad, Reddit appends `?rdt_cid=XXX` to your
 * landing URL. Meta appends `?fbclid=YYY`. Capturing these on first
 * touch and persisting them lets us attach them to the user on signup
 * — which then lets server-side Conversions API (CAPI) attribute
 * conversions back to the exact ad click with 100% certainty (vs
 * heuristic email/IP matching that's much less reliable).
 *
 * Browser pixels don't need these click IDs (they cookie automatically).
 * This is purely prep for server-side CAPI, which we will add later
 * when ad spend justifies it. Until then the IDs are stored but unused
 * — harmless and forward-compatible.
 *
 * Strategy:
 *  - Capture on every page mount (handles deep links too)
 *  - Only overwrite stored values when new params are present — keeps
 *    the first-touch click ID if the user later visits without it
 *  - 90-day expiry so we don't attach a stale click ID to a return
 *    visit months later
 */

const STORAGE_KEY = 'elabs_ad_click_ids';
const MAX_AGE_MS = 90 * 24 * 60 * 60 * 1000; // 90 days

export interface AdClickIds {
  rdt_cid?: string;  // Reddit Ads click ID
  fbclid?: string;   // Meta (Facebook) click ID
  gclid?: string;    // Google Ads click ID (free to capture too)
  capturedAt?: number;
}

/**
 * Read the current URL for ad click-ID query params and persist any
 * found values. Idempotent — safe to call on every page mount.
 */
export function captureClickIds(): void {
  if (typeof window === 'undefined') return;
  try {
    const params = new URLSearchParams(window.location.search);
    const incoming: AdClickIds = {};
    const rdt  = params.get('rdt_cid');
    const fb   = params.get('fbclid');
    const gcl  = params.get('gclid');
    if (rdt) incoming.rdt_cid = rdt;
    if (fb)  incoming.fbclid  = fb;
    if (gcl) incoming.gclid   = gcl;
    if (!incoming.rdt_cid && !incoming.fbclid && !incoming.gclid) return;

    const existing = readClickIds();
    const merged: AdClickIds = {
      // Keep first-touch values unless new params arrive — overwriting
      // would clobber a real ad-click ID with a later organic visit.
      rdt_cid: incoming.rdt_cid || existing.rdt_cid,
      fbclid:  incoming.fbclid  || existing.fbclid,
      gclid:   incoming.gclid   || existing.gclid,
      capturedAt: Date.now(),
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(merged));
  } catch {
    /* storage disabled / quota exceeded — silent */
  }
}

/**
 * Read currently-stored click IDs. Returns {} if none / expired / unreadable.
 */
export function readClickIds(): AdClickIds {
  if (typeof window === 'undefined') return {};
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as AdClickIds;
    if (parsed.capturedAt && Date.now() - parsed.capturedAt > MAX_AGE_MS) {
      // Expired — drop it
      localStorage.removeItem(STORAGE_KEY);
      return {};
    }
    return parsed;
  } catch {
    return {};
  }
}

/**
 * Read click IDs in a form ready to send to the backend on register/login.
 * Returns null if nothing is stored, so callers can `if (clickIds)` cheaply.
 */
export function clickIdsForRegister(): AdClickIds | null {
  const ids = readClickIds();
  if (!ids.rdt_cid && !ids.fbclid && !ids.gclid) return null;
  return ids;
}
